import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ForgotPasswordStep = 'forgot' | 'verify' | 'reset';

export interface CustomerDetail {
  customerID: number;
  customerTypeID: number;
}

export interface ForgotPasswordSlice {
  currentStep: ForgotPasswordStep;
  customerEmail: string;
  customerDetail: CustomerDetail | null;
}

const loadState = (): ForgotPasswordSlice | undefined => {
  if (typeof window === 'undefined') return undefined; // SSR safety

  try {
    const serializedState = localStorage.getItem('forgotPassword');
    return serializedState ? JSON.parse(serializedState) : undefined;
  } catch (error) {
    console.error('Failed to load forgot password state from localStorage:', error);
    return undefined;
  }
};

const saveState = (state: ForgotPasswordSlice) => {
  if (typeof window === 'undefined') return; // Skip on server

  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('forgotPassword', serializedState);
  } catch (error) {
    console.error('Failed to save forgot password state to localStorage:', error);
  }
};

const getFreshInitialState = (): ForgotPasswordSlice => ({
  currentStep: 'forgot',
  customerEmail: '',
  customerDetail: null,
});

export const forgotPasswordInitialState: ForgotPasswordSlice = loadState() || getFreshInitialState();

const forgotPasswordSlice = createSlice({
  name: 'forgotPassword',
  initialState: forgotPasswordInitialState,
  reducers: {
    setCurrentStep(state, action: PayloadAction<ForgotPasswordStep>) {
      state.currentStep = action.payload;
      saveState(state);
    },
    setCustomerEmail(state, action: PayloadAction<string>) {
      state.customerEmail = action.payload;
      saveState(state);
    },
    setCustomerDetail(state, action: PayloadAction<CustomerDetail | null>) {
      state.customerDetail = action.payload;
      saveState(state);
    },
    resetForgotPasswordState() {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('forgotPassword');
      }
      return getFreshInitialState();
    },
    removeForgotPasswordFromLocalStorageState() {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('forgotPassword');
      }
    },
  },
});

export const {
  setCurrentStep,
  setCustomerEmail,
  setCustomerDetail,
  resetForgotPasswordState,
  removeForgotPasswordFromLocalStorageState,
} = forgotPasswordSlice.actions;

export default forgotPasswordSlice;
